<template>
	<div class="reset-password">

		<a-row type="flex" :gutter="[24,24]" justify="space-around" align="middle">

			<!-- Reset Password Form Column -->
			<a-col :span="24" :md="12" :lg="{span: 12, offset: 0}" :xl="{span: 6, offset: 2}" class="col-form">
				<h1 class="mb-15">Reset Password</h1>
				<h5 class="font-regular text-muted">
					{{ submitted ? 'Password successfully reset!' : 'Enter your new password' }}
				</h5>

				<!-- Loading State -->
				<div v-if="validating" class="text-center mb-20">
					<a-spin size="large" />
					<p class="mt-10 text-muted">Validating reset link...</p>
				</div>

				<!-- Invalid Token Alert -->
				<a-alert
					v-if="!validating && !tokenValid"
					type="error"
					:message="errorMessage"
					description="This reset link is invalid or has expired. Please request a new one."
					show-icon
					class="mb-20"
				/>

				<!-- Success Message -->
				<a-alert
					v-if="submitted"
					type="success"
					message="Password Reset Successful!"
					description="Your password has been successfully reset. You can now sign in with your new password."
					show-icon
					class="mb-20"
				/>

				<!-- Reset Password Form -->
				<a-form
					v-if="!validating && tokenValid && !submitted"
					id="reset-password-form"
					:form="form"
					class="reset-password-form"
					@submit="handleSubmit"
					:hideRequiredMark="true"
				>
					<a-form-item class="mb-10" label="Email" :colon="false">
						<a-input
							:value="email"
							disabled
							placeholder="Email"
						/>
					</a-form-item>

					<a-form-item class="mb-10" label="New Password" :colon="false">
						<a-input
							v-decorator="[
								'newPassword',
								{ rules: [
									{ required: true, message: 'Please input your new password!' },
									{ min: 8, message: 'Password must be at least 8 characters!' }
								] },
							]"
							type="password"
							placeholder="New Password"
						/>
					</a-form-item>

					<a-form-item class="mb-10" label="Confirm Password" :colon="false">
						<a-input
							v-decorator="[
								'confirmPassword',
								{ rules: [
									{ required: true, message: 'Please confirm your password!' },
									{ validator: validatePasswordMatch }
								] },
							]"
							type="password"
							placeholder="Confirm Password"
						/>
					</a-form-item>

					<a-form-item>
						<a-button
							type="primary"
							block
							html-type="submit"
							class="reset-password-button"
							:loading="loading"
						>
							{{ loading ? 'RESETTING...' : 'RESET PASSWORD' }}
						</a-button>
					</a-form-item>
				</a-form>
				<!-- / Reset Password Form -->

				<!-- Action Buttons -->
				<div class="text-center mt-20">
					<a-button
						v-if="!validating && !tokenValid"
						type="primary"
						block
						@click="goToForgotPassword"
					>
						REQUEST NEW RESET LINK
					</a-button>

					<a-button
						v-if="submitted"
						type="primary"
						block
						@click="goToSignIn"
					>
						GO TO SIGN IN
					</a-button>

					<div v-if="!submitted" class="mt-15">
						<span class="text-muted">Remember your password? </span>
						<router-link to="/sign-in" class="font-bold">Sign In</router-link>
					</div>
				</div>
			</a-col>
			<!-- / Reset Password Form Column -->

			<!-- Image Column -->
			<a-col :span="24" :md="12" :lg="12" :xl="12" class="col-img">
				<img src="images/zerocents-logo.png" alt="">
			</a-col>
			<!-- / Image Column -->

		</a-row>

	</div>
</template>

<script>
export default {
	data() {
		return {
			loading: false,
			validating: true,
			tokenValid: false,
			submitted: false,
			email: '',
			token: '',
			errorMessage: 'Invalid or expired reset token'
		}
	},
	beforeCreate() {
		// Creates the form and adds to it component's "form" property.
		this.form = this.$form.createForm(this, { name: 'reset_password' });
	},
	async mounted() {
		// Get token from URL query parameter
		this.token = this.$route.query.token;

		if (!this.token) {
			this.validating = false;
			this.tokenValid = false;
			this.errorMessage = 'No reset token provided';
			return;
		}

		// Validate token
		await this.validateToken();
	},
	methods: {
		async validateToken() {
			try {
				const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
				const response = await fetch(`${apiBaseUrl}/api/auth/reset-password/${this.token}`);

				if (!response.ok) {
					throw new Error('Invalid token');
				}

				const data = await response.json();

				if (data.valid) {
					this.tokenValid = true;
					this.email = data.email;
				} else {
					this.tokenValid = false;
					this.errorMessage = data.message || 'Invalid or expired reset token';
				}
			} catch (error) {
				console.error('Error validating token:', error);
				this.tokenValid = false;
				this.errorMessage = 'Failed to validate reset token';
			} finally {
				this.validating = false;
			}
		},
		validatePasswordMatch(rule, value, callback) {
			const newPassword = this.form.getFieldValue('newPassword');
			if (value && value !== newPassword) {
				callback('Passwords do not match!');
			} else {
				callback();
			}
		},
		async handleSubmit(e) {
			e.preventDefault();
			this.form.validateFields(async (err, values) => {
				if (!err) {
					this.loading = true;
					try {
						const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
						const response = await fetch(`${apiBaseUrl}/api/auth/reset-password`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								token: this.token,
								newPassword: values.newPassword,
							}),
						});

						if (!response.ok) {
							const errorData = await response.json();
							throw new Error(errorData.message || 'Failed to reset password');
						}

						// Show success
						this.submitted = true;

						// Optionally redirect after a delay
						setTimeout(() => {
							this.$router.push('/sign-in');
						}, 3000);

					} catch (error) {
						console.error('Error resetting password:', error);
						this.$notification.error({
							message: 'Error',
							description: error.message || 'Failed to reset password. Please try again.',
							duration: 5,
						});
					} finally {
						this.loading = false;
					}
				}
			});
		},
		goToSignIn() {
			this.$router.push('/sign-in');
		},
		goToForgotPassword() {
			this.$router.push('/forgot-password');
		}
	},
}
</script>

<style lang="scss" scoped>
	body {
		background-color: #ffffff;
	}

	.reset-password {
		min-height: 100vh;
		display: flex;
		align-items: center;
		background-color: #ffffff;
		padding: 0 20px;

		.text-center {
			text-align: center;
		}

		.font-bold {
			font-weight: 600;
			color: #1890ff;

			&:hover {
				color: #40a9ff;
			}
		}
	}

	.col-img {
		img {
			max-width: 100%;
			height: auto;
			display: block;
		}
	}
</style>
